import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom'; // Import Link for navigation
import { useAuth } from '../context/AuthContext'; // Use the hook providing the implemented login

const Login = () => {
    const [credentials, setCredentials] = useState({ email: '', password: '' });
    const [error, setError] = useState<string | null>(null); // Use null for no error state
    const [loading, setLoading] = useState(false);
    const { login } = useAuth(); // Get the login function from the context
    const navigate = useNavigate();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCredentials(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null); // Clear previous errors
        setLoading(true);

        try {
            // Call the login function from AuthContext
            const loggedInUser = await login(credentials.email, credentials.password);

            // --- Role-Based Redirection ---
            // Access the role from the user object returned by the context's login function
            const rolesPermissions = JSON.parse(localStorage.getItem('rolesPermissions') || '{}');
            console.log("User role:", rolesPermissions); // Debugging line to check the role
            const userRole = rolesPermissions?.roles[0]; // Make sure 'role' exists on your User type


            // Use lowercase comparison for robustness
            if (userRole && (userRole.toLowerCase() === 'super_admin' || userRole.toLowerCase() === 'staff')) {
                console.log(`Redirecting user with role: ${userRole} to customer management`);
                navigate('/customer-management'); // Redirect admins/staff
            } else {
                console.log(`Redirecting user with role: ${userRole || 'N/A'} to home`);
                navigate('/'); // Redirect other users to the home page
            }
            // --- End Redirection Logic ---

        } catch (err: any) {
            console.error("Login page handleSubmit failed:", err);

            // Set a user-friendly error message
            let errorMessage = 'Login failed. Please check your credentials or try again later.';
            // You can customize this based on the error status like in your commented code if needed
            if (err.response?.status === 401) {
                errorMessage = 'Invalid email or password.';
            } else if (err.message) {
                // Handle specific error messages if available
                errorMessage = err.message;
            }
            setError(errorMessage);

        } finally {
            setLoading(false);
        }
    };

    // Using the cleaner form structure from your code
    return (
        <div className="container mx-auto p-4 max-w-md mt-8"> {/* Added margin-top */}
            <h1 className="text-2xl font-bold mb-6 text-center">Login</h1> {/* Centered title */}
            <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-8"> {/* Added form container styling */}
                <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="email">Email</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={credentials.email}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300" // Added focus styles
                        autoComplete="email"
                    />
                </div>
                <div className="mb-6"> {/* Increased margin */}
                    <label className="block text-gray-700 mb-2" htmlFor="password">Password</label>
                    <input
                        type="password"
                        id="password"
                        name="password"
                        value={credentials.password}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300" // Added focus styles
                        autoComplete="current-password"
                    />
                </div>
                {error && <p className="text-red-500 text-sm mb-4">{error}</p>} {/* Adjusted error styling */}
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed transition duration-200" // Added disabled cursor
                >
                    {loading ? 'Logging in...' : 'Login'}
                </button>
                <p className="mt-4 text-center text-sm">
                    Don't have an account? <Link to="/register" className="text-blue-500 hover:underline">Register</Link>
                </p>
                <p className="mt-2 text-center text-sm">
                    <Link to="/forgot-password" className="text-blue-500 hover:underline">Forgot Password?</Link>
                </p>
            </form>
        </div>
    );
};

export default Login;
